import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface Box {
  id: string;
  name: string;
  category: string;
  location: string;
  status: 'stored' | 'in-transit' | 'delivered';
  priority: 'low' | 'medium' | 'high';
  tags: string[];
  qrCode: string;
  description: string;
  createdAt: string;
}

export default function Boxes() {
  const navigate = useNavigate();
  const { user, loading: authLoading, signOut } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [editingBox, setEditingBox] = useState<any>(null);
  const [viewingBox, setViewingBox] = useState<any>(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    category: '',
    location: '',
    priority: 'medium' as 'low' | 'medium' | 'high',
    tags: [] as string[],
    description: ''
  });

  const [boxes] = useState<Box[]>([
    {
      id: '1',
      name: 'Kış Kıyafetleri',
      category: 'Giyim',
      location: 'Depo A - Raf 3',
      status: 'stored',
      priority: 'low',
      tags: ['Kış', 'Giysi', 'Mevsimlik'],
      qrCode: 'QR-2024-001',
      description: 'Mont, kazak ve kışlık aksesuarlar',
      createdAt: '2024-01-15'
    },
    {
      id: '2',
      name: 'Elektronik Eşyalar',
      category: 'Elektronik',
      location: 'Depo B - Raf 1',
      status: 'stored',
      priority: 'high',
      tags: ['Değerli', 'Kırılabilir', 'Elektronik'],
      qrCode: 'QR-2024-002',
      description: 'Eski laptop, tablet ve aksesuarlar',
      createdAt: '2024-01-20'
    },
    {
      id: '3',
      name: 'Kitaplar ve Dergiler',
      category: 'Kitap',
      location: 'Depo A - Raf 5',
      status: 'stored',
      priority: 'medium',
      tags: ['Kitap', 'Eğitim', 'Koleksiyon'],
      qrCode: 'QR-2024-003',
      description: 'Üniversite kitapları ve dergi koleksiyonu',
      createdAt: '2024-02-01'
    },
    {
      id: '4',
      name: 'Mutfak Eşyaları',
      category: 'Ev Eşyası',
      location: 'Nakliye - Araç 5',
      status: 'in-transit',
      priority: 'medium',
      tags: ['Mutfak', 'Kırılabilir', 'Taşınma'],
      qrCode: 'QR-2024-004',
      description: 'Tabak, bardak ve mutfak gereçleri',
      createdAt: '2024-02-10'
    },
    {
      id: '5',
      name: 'Spor Malzemeleri',
      category: 'Spor',
      location: 'Depo C - Raf 2',
      status: 'stored',
      priority: 'low',
      tags: ['Spor', 'Fitness', 'Outdoor'],
      qrCode: 'QR-2024-005',
      description: 'Dambıl, yoga matı ve spor ekipmanları',
      createdAt: '2024-02-15'
    },
    {
      id: '6',
      name: 'Oyuncaklar',
      category: 'Çocuk',
      location: 'Depo A - Raf 1',
      status: 'stored',
      priority: 'low',
      tags: ['Çocuk', 'Oyuncak', 'Eğlence'],
      qrCode: 'QR-2024-006',
      description: 'Çocuk oyuncakları ve eğitici materyaller',
      createdAt: '2024-02-20'
    }
  ]);

  const filteredBoxes = boxes.filter(box => {
    const matchesSearch = box.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         box.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || box.category === selectedCategory;
    const matchesStatus = selectedStatus === 'all' || box.status === selectedStatus;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const categories = ['all', ...Array.from(new Set(boxes.map(box => box.category)))];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'stored': return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'in-transit': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'delivered': return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
      default: return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'stored': return 'Depolandı';
      case 'in-transit': return 'Nakliyede';
      case 'delivered': return 'Teslim Edildi';
      default: return status;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-400';
      case 'medium': return 'text-yellow-400';
      case 'low': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  const handleEditClick = (box: Box) => {
    setEditingBox(box);
    setEditFormData({
      name: box.name,
      category: box.category,
      location: box.location,
      priority: box.priority,
      tags: [...box.tags],
      description: box.description
    });
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setEditFormData({
      ...editFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleTagRemove = (tagToRemove: string) => {
    setEditFormData({
      ...editFormData,
      tags: editFormData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleTagAdd = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const input = e.currentTarget;
      const newTag = input.value.trim();
      if (newTag && !editFormData.tags.includes(newTag)) {
        setEditFormData({
          ...editFormData,
          tags: [...editFormData.tags, newTag]
        });
        input.value = '';
      }
    }
  };

  const handleSaveEdit = () => {
    console.log('Saving box:', editingBox?.id, editFormData);
    setEditingBox(null);
  };

  const handleDeleteBox = (boxId: string) => {
    const confirmDelete = window.confirm('Bu koliyi silmek istediğinizden emin misiniz?');
    if (confirmDelete) {
      console.log('Deleting box:', boxId);
      // Here you would typically call an API to delete the box
      // For now, we'll just show a success message
      alert('Koli başarıyla silindi!');
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  const totalBoxes = boxes.length;
  const storedBoxes = boxes.filter(b => b.status === 'stored').length;
  const inTransitBoxes = boxes.filter(b => b.status === 'in-transit').length;
  const highPriorityBoxes = boxes.filter(b => b.priority === 'high').length;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#121C2E' }}>
      {/* Floating Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-white/5 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-blue-400/10 rounded-full blur-xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-40 left-1/4 w-40 h-40 bg-white/5 rounded-full blur-xl animate-pulse delay-2000"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 bg-white/10 backdrop-blur-md shadow-sm sticky top-0 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="text-2xl font-bold text-white cursor-pointer">
                pad<span className="text-orange-500">baq</span>
              </Link>
            </div>
            <nav className="flex items-center space-x-4">
              <Link to="/dashboard" className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">Panel</Link>
              <Link to="/spaces" className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">Alanlarım</Link>
              <Link to="/boxes" className="text-orange-500 font-medium px-4 py-2 rounded-lg bg-white/10 whitespace-nowrap cursor-pointer">Koli Yönetimi</Link>
              <Link to="/profile" className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">Profil</Link>
              <button 
                onClick={handleLogout}
                className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-logout-circle-line mr-2"></i>
                Çıkış
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Koli Yönetimi</h1>
              <p className="text-white/60">Eşyalarınızı dijital ortamda takip edin</p>
            </div>
            <button
              onClick={() => navigate('/boxes/new')}
              className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-2"></i>
              Yeni Koli Ekle
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 backdrop-blur-md rounded-2xl p-6 border border-blue-500/30">
              <div className="flex items-center justify-between mb-2">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-500/30 rounded-xl">
                  <i className="ri-archive-line text-2xl text-blue-300"></i>
                </div>
                <span className="text-3xl font-bold text-white">{totalBoxes}</span>
              </div>
              <p className="text-blue-200 font-medium">Toplam Koli</p>
            </div>

            <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 backdrop-blur-md rounded-2xl p-6 border border-green-500/30">
              <div className="flex items-center justify-between mb-2">
                <div className="w-12 h-12 flex items-center justify-center bg-green-500/30 rounded-xl">
                  <i className="ri-checkbox-circle-line text-2xl text-green-300"></i>
                </div>
                <span className="text-3xl font-bold text-white">{storedBoxes}</span>
              </div>
              <p className="text-green-200 font-medium">Depolandı</p>
            </div>

            <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 backdrop-blur-md rounded-2xl p-6 border border-purple-500/30">
              <div className="flex items-center justify-between mb-2">
                <div className="w-12 h-12 flex items-center justify-center bg-purple-500/30 rounded-xl">
                  <i className="ri-truck-line text-2xl text-purple-300"></i>
                </div>
                <span className="text-3xl font-bold text-white">{inTransitBoxes}</span>
              </div>
              <p className="text-purple-200 font-medium">Nakliyede</p>
            </div>

            <div className="bg-gradient-to-br from-red-500/20 to-red-600/20 backdrop-blur-md rounded-2xl p-6 border border-red-500/30">
              <div className="flex items-center justify-between mb-2">
                <div className="w-12 h-12 flex items-center justify-center bg-red-500/30 rounded-xl">
                  <i className="ri-alarm-warning-line text-2xl text-red-300"></i>
                </div>
                <span className="text-3xl font-bold text-white">{highPriorityBoxes}</span>
              </div>
              <p className="text-red-200 font-medium">Yüksek Öncelik</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-white/50 text-lg"></i>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Koli veya etiket ara..."
                className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-white placeholder-white/50 text-sm"
              />
            </div>

            {/* Category Filter */}
            <div className="relative">
              <i className="ri-folder-line absolute left-4 top-1/2 -translate-y-1/2 text-white/50 text-lg"></i>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full pl-12 pr-8 py-3 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-white cursor-pointer appearance-none text-sm"
              >
                <option value="all">Tüm Kategoriler</option>
                {categories.filter(c => c !== 'all').map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-white/50 pointer-events-none"></i>
            </div>

            {/* Status Filter */}
            <div className="relative">
              <i className="ri-filter-line absolute left-4 top-1/2 -translate-y-1/2 text-white/50 text-lg"></i>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full pl-12 pr-8 py-3 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-white cursor-pointer appearance-none text-sm"
              >
                <option value="all">Tüm Durumlar</option>
                <option value="stored">Depolandı</option>
                <option value="in-transit">Nakliyede</option>
                <option value="delivered">Teslim Edildi</option>
              </select>
              <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-white/50 pointer-events-none"></i>
            </div>
          </div>
        </div>

        {/* Boxes Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBoxes.map((box) => (
            <div key={box.id} className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/10 hover:border-orange-500/50 transition-all">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-1">{box.name}</h3>
                  <p className="text-white/60 text-sm">{box.category}</p>
                </div>
                <div className={`px-3 py-1 rounded-full border text-xs font-medium whitespace-nowrap ${getStatusColor(box.status)}`}>
                  {getStatusText(box.status)}
                </div>
              </div>

              {/* Info */}
              <div className="space-y-3 mb-4">
                <div className="flex items-center text-white/70 text-sm">
                  <i className="ri-map-pin-line mr-2 text-orange-400"></i>
                  {box.location}
                </div>
                <div className="flex items-center text-white/70 text-sm">
                  <i className="ri-qr-code-line mr-2 text-blue-400"></i>
                  {box.qrCode}
                </div>
                <div className="flex items-center text-sm">
                  <i className={`ri-flag-line mr-2 ${getPriorityColor(box.priority)}`}></i>
                  <span className={getPriorityColor(box.priority)}>
                    {box.priority === 'high' ? 'Yüksek' : box.priority === 'medium' ? 'Orta' : 'Düşük'} Öncelik
                  </span>
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                {box.tags.map((tag, index) => (
                  <span key={index} className="px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full text-xs">
                    {tag}
                  </span>
                ))}
              </div>

              {/* Description */}
              <p className="text-white/60 text-sm mb-4 line-clamp-2">{box.description}</p>

              {/* Actions */}
              <div className="flex items-center justify-between gap-2 mt-4 pt-4 border-t border-white/10">
                <button
                  onClick={() => setViewingBox(box)}
                  className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-300 rounded-lg transition-colors cursor-pointer text-sm whitespace-nowrap"
                >
                  <i className="ri-eye-line"></i>
                </button>
                <button
                  onClick={() => setEditingBox(box)}
                  className="px-4 py-2 bg-orange-500/20 hover:bg-orange-500/30 text-orange-300 rounded-lg transition-colors cursor-pointer text-sm whitespace-nowrap"
                >
                  <i className="ri-edit-line"></i>
                </button>
                <button
                  onClick={() => handleDeleteBox(box.id)}
                  className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-colors cursor-pointer text-sm whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredBoxes.length === 0 && (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-inbox-line text-5xl text-white/30"></i>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Koli Bulunamadı</h3>
            <p className="text-white/60 mb-6">Arama kriterlerinize uygun koli bulunamadı</p>
            <button
              onClick={() => navigate('/boxes/new')}
              className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-2"></i>
              İlk Kolinizi Ekleyin
            </button>
          </div>
        )}
      </main>

      {/* View Box Modal */}
      {viewingBox && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a2942] backdrop-blur-md rounded-2xl p-8 border border-white/20 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">Koli Detayları</h3>
              <button
                onClick={() => setViewingBox(null)}
                className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl text-white"></i>
              </button>
            </div>

            <div className="space-y-6">
              {/* QR Code */}
              <div className="flex justify-center">
                <div className="bg-white p-4 rounded-xl">
                  <div className="w-48 h-48 flex items-center justify-center">
                    <i className="ri-qr-code-line text-8xl text-gray-800"></i>
                  </div>
                  <p className="text-center text-sm text-gray-600 mt-2 font-mono">{viewingBox.qrCode}</p>
                </div>
              </div>

              {/* Box Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">Koli Adı</label>
                  <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white">
                    {viewingBox.name}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">Kategori</label>
                  <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white">
                    <i className={`${getCategoryIcon(viewingBox.category)} mr-2`}></i>
                    {viewingBox.category}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">Durum</label>
                  <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-3">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(viewingBox.status)}`}>
                      <i className={`${viewingBox.status === 'stored' ? 'ri-archive-line' : 'ri-truck-line'} mr-1`}></i>
                      {getStatusText(viewingBox.status)}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">Öncelik</label>
                  <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-3">
                    <span className={`inline-flex items-center text-sm font-medium ${getPriorityColor(viewingBox.priority)}`}>
                      <i className="ri-flag-line mr-1"></i>
                      {viewingBox.priority === 'high' ? 'Yüksek' : viewingBox.priority === 'medium' ? 'Orta' : 'Düşük'} Öncelik
                    </span>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-white/70 mb-2">Konum</label>
                  <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white">
                    <i className="ri-map-pin-line mr-2 text-orange-400"></i>
                    {viewingBox.location}
                  </div>
                </div>
              </div>

              {/* Tags */}
              {viewingBox.tags && viewingBox.tags.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">Etiketler</label>
                  <div className="flex flex-wrap gap-2">
                    {viewingBox.tags.map((tag: string, index: number) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full text-sm"
                      >
                        <i className="ri-price-tag-3-line mr-1"></i>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              {viewingBox.description && (
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">Açıklama</label>
                  <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white/80">
                    {viewingBox.description}
                  </div>
                </div>
              )}

              {/* Created Date */}
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">Oluşturulma Tarihi</label>
                <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white/80">
                  <i className="ri-calendar-line mr-2 text-blue-400"></i>
                  {viewingBox.createdAt}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-white/10">
              <button
                onClick={() => setViewingBox(null)}
                className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                Kapat
              </button>
              <button
                onClick={() => {
                  setViewingBox(null);
                  handleEditClick(viewingBox);
                }}
                className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-edit-line mr-2"></i>
                Düzenle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingBox && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a2942] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white/10">
            {/* Modal Header */}
            <div className="sticky top-0 bg-[#1a2942] border-b border-white/10 p-6 flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold text-white">Koli Düzenle</h3>
                <p className="text-white/60 text-sm mt-1">Koli bilgilerini güncelleyin</p>
              </div>
              <button
                onClick={() => setEditingBox(null)}
                className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl text-white"></i>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Koli Adı */}
              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">
                  Koli Adı <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={editFormData.name}
                  onChange={handleEditChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-white placeholder-white/50"
                  placeholder="Örn: Kış Kıyafetleri"
                />
              </div>

              {/* Kategori ve Öncelik */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/90 mb-2">
                    Kategori <span className="text-red-400">*</span>
                  </label>
                  <select
                    name="category"
                    value={editFormData.category}
                    onChange={handleEditChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-white cursor-pointer pr-8"
                  >
                    <option value="Giyim">Giyim</option>
                    <option value="Elektronik">Elektronik</option>
                    <option value="Kitap">Kitap</option>
                    <option value="Ev Eşyası">Ev Eşyası</option>
                    <option value="Spor">Spor</option>
                    <option value="Çocuk">Çocuk</option>
                    <option value="Diğer">Diğer</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/90 mb-2">
                    Öncelik <span className="text-red-400">*</span>
                  </label>
                  <select
                    name="priority"
                    value={editFormData.priority}
                    onChange={handleEditChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-white cursor-pointer pr-8"
                  >
                    <option value="low">Düşük</option>
                    <option value="medium">Orta</option>
                    <option value="high">Yüksek</option>
                  </select>
                </div>
              </div>

              {/* Konum */}
              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">
                  Konum <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="location"
                  value={editFormData.location}
                  onChange={handleEditChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-white placeholder-white/50"
                  placeholder="Örn: Depo A - Raf 3"
                />
              </div>

              {/* Etiketler */}
              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">
                  Etiketler
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {editFormData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full text-sm flex items-center"
                    >
                      {tag}
                      <button
                        onClick={() => handleTagRemove(tag)}
                        className="ml-2 hover:text-orange-100 cursor-pointer"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  onKeyDown={handleTagAdd}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-white placeholder-white/50"
                  placeholder="Etiket yazıp Enter'a basın"
                />
              </div>

              {/* Açıklama */}
              <div>
                <label className="block text-sm font-medium text-white/90 mb-2">
                  Açıklama
                </label>
                <textarea
                  name="description"
                  value={editFormData.description}
                  onChange={handleEditChange}
                  rows={4}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-white placeholder-white/50 resize-none"
                  placeholder="Koli içeriği hakkında detaylı bilgi..."
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-[#1a2942] border-t border-white/10 p-6 flex items-center justify-end space-x-4">
              <button
                onClick={() => setEditingBox(null)}
                className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                İptal
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-save-line mr-2"></i>
                Değişiklikleri Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function for category icons
function getCategoryIcon(category: string) {
  const icons: Record<string, string> = {
    'Kişisel Eşyalar': 'ri-user-line',
    'Elektronik': 'ri-smartphone-line',
    'Kitaplar': 'ri-book-line',
    'Kıyafetler': 'ri-shirt-line',
    'Mutfak': 'ri-restaurant-line',
    'Diğer': 'ri-more-line',
  };
  return icons[category] || 'ri-box-3-line';
}